import React, { ComponentProps, useState } from "react";
import InputShell from "./InputShell";
import ReactInputMask from "react-input-mask";
import { AiFillCalendar } from "react-icons/ai";
import { Popover } from "@headlessui/react";
import { DayPicker, DayPickerProps, getDefaultClassNames, PropsSingle } from "react-day-picker";
import dayjs from "dayjs";
import { cn } from "@/utils/cn";

const ICON_PLACEMENT_CLASS: Record<Props["iconPlacement"], string> = {
  left: "left-[0.063rem]",
  right: "right-[0.063rem]"
};

type Props = {
  onChange: (newValue: string) => void;
  value: string;
  datePickerProps?: DatePickerProps;
  iconPlacement?: "left" | "right";
} & Omit<ComponentProps<typeof InputShell>, "children"> &
  Omit<ComponentProps<typeof ReactInputMask>, "onChange" | "mask">;

type DatePickerProps = {
  initialValue?: string;
  onChange?: (mdy: string) => void;
  onClose?: () => void;
} & Partial<PropsSingle & DayPickerProps>;

function DatePicker({
  initialValue,
  onChange,
  onClose,
  startMonth,
  endMonth,
  ...rest
}: DatePickerProps) {
  const [selected, setSelected] = useState(
    (() => {
      if (dayjs(new Date(initialValue)).isValid()) return new Date(initialValue);
      return new Date();
    })()
  );
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      mode="single"
      defaultMonth={selected}
      captionLayout="dropdown"
      startMonth={startMonth}
      endMonth={endMonth || new Date()}
      selected={selected}
      onSelect={(newValue) => {
        onChange(dayjs(newValue).format("MM/DD/YYYY"));
        setSelected(newValue);
        onClose();
      }}
      showOutsideDays
      className="p-4"
      classNames={{
        chevron: ``,
        caption_label: `${defaultClassNames.caption_label} px-1`,
        selected: "rounded-full text-white font-bold bg-primary-base",
        outside: "text-gray-300",
        today: `text-primary-base font-bold`
      }}
      {...rest}
    />
  );
}

function InputDate({
  label,
  onChange,
  error,
  value,
  datePickerProps,
  isOptional,
  iconPlacement = "right",
  disabled = false,
  className,
  ...rest
}: Props) {
  const inputShell = {
    label,
    error,
    ...(isOptional
      ? { isOptional, optionalLabel: rest.optionalLabel }
      : { required: rest.required })
  };
  return (
    <InputShell {...inputShell}>
      <Popover as="div" className="relative">
        <ReactInputMask
          className={cn(
            "min-h-[2.5rem] w-full rounded-[0.25rem] border border-outline px-3 text-sm leading-4 focus:outline-none",
            iconPlacement === "left" && "pl-8",
            disabled && "cursor-not-allowed bg-slate-100 text-slate-500",
            className
          )}
          title={label}
          onChange={(e) => onChange(e.target.value)}
          value={value}
          autoComplete="off"
          mask="99/99/9999"
          placeholder="MM/DD/YYYY"
          disabled={disabled}
          {...rest}
        />
        <div className={cn("absolute top-[0.063rem]", ICON_PLACEMENT_CLASS[iconPlacement])}>
          <Popover.Button
            className={cn(
              "flex h-[2.375rem] w-10 rounded-r-[0.25rem] text-[#6D7D96]",
              disabled && "cursor-not-allowed text-slate-500 opacity-50"
            )}
            type="button"
            title="Open Datepicker"
            disabled={disabled}
          >
            <AiFillCalendar className="m-auto h-4 w-4" />
          </Popover.Button>
        </div>
        <Popover.Panel>
          {({ close }) => (
            <div
              className={cn(
                "absolute z-20 translate-y-1 rounded border bg-white shadow-modal",
                iconPlacement === "left" ? "left-0" : "right-0 "
              )}
            >
              <DatePicker
                initialValue={value || ""}
                onChange={onChange}
                onClose={() => close()}
                {...datePickerProps}
              />
            </div>
          )}
        </Popover.Panel>
      </Popover>
    </InputShell>
  );
}

export default InputDate;
